import { clsx } from "clsx";
import {
  forwardRef,
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { RequiredMark } from "../required-mark";
import { Text } from "../typography";

export type InputType =
  | "text"
  | "email"
  | "password"
  | "tel"
  | "number"
  | "search"
  | "url"
  | "date"
  | "datetime-local"
  | "time";

export interface InputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "type" | "size" | "onChange" | "prefix"
  > {
  label?: string;
  helperText?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onRightIconClick?: () => void;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  type?: InputType;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  wrapperClassName?: string;
  /** Applied to the inner border wrapper div — use to override height/padding/radius for compact variants. */
  inputWrapperClassName?: string;
  /** Renders a <textarea> instead of <input>. */
  multiline?: boolean;
  /** Number of visible rows when multiline is true. */
  rows?: number;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    error,
    leftIcon,
    rightIcon,
    onRightIconClick,
    leftAddon,
    rightAddon,
    type = "text",
    disabled = false,
    required = false,
    multiline = false,
    rows = 3,
    className,
    wrapperClassName,
    inputWrapperClassName,
    id: idProp,
    onChange,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const [revealed, setRevealed] = useState(false);

  const isPassword = type === "password";
  const effectiveType = isPassword && revealed ? "text" : type;

  const resolvedRightIcon =
    rightIcon ??
    (isPassword ? (
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? "Hide password" : "Show password"}
        className="inline-flex size-6 items-center justify-center text-textBody hover:text-textDark"
      >
        {revealed ? (
          <IconEyeOff size={20} />
        ) : (
          <IconEye size={20} />
        )}
      </button>
    ) : null);

  const hasError = Boolean(error);
  const describedById = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const wrapperClass = clsx(
    "flex items-start gap-1.5 rounded-2xl border px-3 py-4",
    "bg-bgWhite transition-colors",
    "focus-within:border-textBody",
    hasError ? "border-accentError" : "border-borderDefault",
    disabled && "opacity-60",
    multiline ? "h-auto" : "h-[54px] items-center",
    inputWrapperClassName,
  );

  return (
    <div className={clsx("flex w-full flex-col gap-1.5", wrapperClassName)}>
      {label ? (
        <label htmlFor={id} className="px-1.5">
          <Text variant="bodySmBold" inline tone="body">
            {label}
            {required ? <RequiredMark /> : null}
          </Text>
        </label>
      ) : null}

      <div className={wrapperClass}>
        {leftIcon ? (
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center text-textBody">
            {leftIcon}
          </span>
        ) : null}

        {leftAddon ? <span className="flex shrink-0 items-center gap-1.5">{leftAddon}</span> : null}

        {multiline ? (
          <textarea
            id={id}
            rows={rows}
            disabled={disabled}
            required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={describedById}
            onChange={(e) => onChange?.(e as unknown as ChangeEvent<HTMLInputElement>)}
            className={clsx(
              "min-w-0 flex-1 resize-none border-0 bg-transparent p-0 outline-none",
              "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
              "text-bodySm font-bold text-textDark",
              "placeholder:font-normal placeholder:text-textBody",
              "disabled:cursor-not-allowed disabled:placeholder:text-textMuted",
              className,
            )}
            {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            id={id}
            ref={ref}
            type={effectiveType}
            disabled={disabled}
            required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={describedById}
            onChange={onChange}
            className={clsx(
              "min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 outline-none",
              "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
              "text-bodySm font-bold text-textDark",
              "placeholder:font-normal placeholder:text-textBody",
              "disabled:cursor-not-allowed disabled:placeholder:text-textMuted",
              className,
            )}
            {...rest}
          />
        )}

        {!multiline && rightAddon ? (
          <span className="flex shrink-0 items-center gap-1.5">{rightAddon}</span>
        ) : null}

        {!multiline && resolvedRightIcon ? (
          <span
            className={clsx(
              "inline-flex shrink-0 items-center justify-center text-textBody",
              onRightIconClick && "cursor-pointer",
            )}
            onClick={onRightIconClick}
          >
            {resolvedRightIcon}
          </span>
        ) : null}
      </div>

      {hasError ? (
        <span id={`${id}-error`} className="px-3">
          <Text variant="bodySmBold" inline tone="error">
            {error}
          </Text>
        </span>
      ) : helperText ? (
        <span id={`${id}-helper`} className="px-3">
          <Text variant="bodySm" tone="muted" inline>
            {helperText}
          </Text>
        </span>
      ) : null}
    </div>
  );
});
